import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AgentTokenService } from '../../services/agent-token.service';
import { TenantUserService } from '../../services/tenant-user.service';
import { CustomerUserDTO } from '../../models/customer.model';
import { CreatePatRequest, PatDTO } from '../../models/pat.model';
import { isAgentAccount, visibleAddress } from '../../shared/account-kind';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { KiKontoPillComponent } from '../../components/ki-konto-pill/ki-konto-pill.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { PatCreateDialogComponent, PatScopesSource } from '../../account/pat-create-dialog/pat-create-dialog.component';

/** Tokens of one agent account, issued and revoked by the platform admin (NEX-1980). */
@Component({
  selector: 'app-agent-tokens-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    DatePipe,
    MatButton,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    PageHeaderComponent,
    KiKontoPillComponent,
    ConfirmDialogComponent,
    PatCreateDialogComponent,
  ],
  templateUrl: './agent-tokens-page.component.html',
  styleUrl: './agent-tokens-page.component.scss',
})
export class AgentTokensPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly agentTokenService = inject(AgentTokenService);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly snackSuccess: MatSnackBarConfig = { panelClass: 'snackbar-success' };
  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error' };

  private readonly customerId = this.route.snapshot.paramMap.get('customerId') ?? '';
  private readonly userId = this.route.snapshot.paramMap.get('userId') ?? '';

  readonly user = signal<CustomerUserDTO | null>(null);
  readonly userLoading = signal(true);
  readonly userLoadFailed = signal(false);

  readonly tokens = signal<PatDTO[]>([]);
  readonly tokensLoading = signal(true);
  readonly tokensLoadFailed = signal(false);

  readonly createDialogVisible = signal(false);
  readonly createdToken = signal<string | null>(null);
  readonly isCreatingToken = signal(false);
  readonly revokeTarget = signal<PatDTO | null>(null);
  readonly isRevoking = signal(false);

  readonly isAgent = computed(() => {
    const user = this.user();
    return user !== null && isAgentAccount(user);
  });
  readonly isInactive = computed(() => this.user()?.status === 'INACTIVE');
  readonly address = computed(() => {
    const user = this.user();
    return user ? visibleAddress(user) : '';
  });

  readonly scopesSource: PatScopesSource = () => this.agentTokenService.getTokenScopes(this.customerId);

  ngOnInit(): void {
    this.tenantUserService
      .getUser(this.customerId, this.userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.user.set(user);
          this.userLoading.set(false);
          if (isAgentAccount(user)) this.loadTokens();
        },
        error: () => {
          this.userLoading.set(false);
          this.userLoadFailed.set(true);
        },
      });
  }

  openCreateDialog(): void {
    if (this.isInactive()) return;
    this.createdToken.set(null);
    this.createDialogVisible.set(true);
  }

  onCreateDialogVisibleChange(visible: boolean): void {
    this.createDialogVisible.set(visible);
    if (!visible) this.createdToken.set(null);
  }

  onCreateToken(request: CreatePatRequest): void {
    const expiresAt = request.expiresAt;
    if (!expiresAt) return;
    this.isCreatingToken.set(true);
    this.agentTokenService
      .createToken(this.customerId, this.userId, { name: request.name, scopes: request.scopes, expiresAt })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isCreatingToken.set(false);
          this.createdToken.set(response.token);
          this.tokens.update((tokens) => [response.pat, ...tokens]);
        },
        error: (error: unknown) => {
          this.isCreatingToken.set(false);
          const key =
            error instanceof HttpErrorResponse && error.status === 409 ? 'pat.createNameExists' : 'pat.createError';
          this.snackBar.open(this.translate.instant(key), undefined, this.snackError);
        },
      });
  }

  requestRevoke(token: PatDTO): void {
    this.revokeTarget.set(token);
  }

  cancelRevoke(): void {
    this.revokeTarget.set(null);
  }

  confirmRevoke(): void {
    const target = this.revokeTarget();
    if (!target) return;
    this.isRevoking.set(true);
    this.agentTokenService
      .revokeToken(this.customerId, this.userId, target.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isRevoking.set(false);
          this.tokens.update((tokens) => tokens.filter((token) => token.id !== target.id));
          this.revokeTarget.set(null);
          this.snackBar.open(this.translate.instant('pat.revokeSuccess'), undefined, this.snackSuccess);
        },
        error: () => {
          this.isRevoking.set(false);
          this.revokeTarget.set(null);
          this.snackBar.open(this.translate.instant('pat.revokeError'), undefined, this.snackError);
        },
      });
  }

  private loadTokens(): void {
    this.tokensLoading.set(true);
    this.agentTokenService
      .listTokens(this.customerId, this.userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokens) => {
          this.tokens.set(tokens);
          this.tokensLoading.set(false);
        },
        error: () => {
          this.tokensLoading.set(false);
          this.tokensLoadFailed.set(true);
        },
      });
  }
}
